import React, { useState } from 'react';
import HarbourModal from './HarbourModal';
import HarbourNotice from './HarbourNotice';
import { HarbourField } from './HarbourField';
import { apiUpdateGuardianDetails } from '../../utils/rmsApi';
import {
  parentDetailsErrors,
  parentEmailChanged,
  parentFullName,
  parentHasSignedIn,
  parentWasInvited,
  relationshipOptionsFor,
} from '../../utils/parentDetails';
import './harbour.css';

/*
 * "Change parent or carer" (TDI-622, R-005), built on the "Change teacher"
 * modal's layout (design 4153:286473; no parent frame exists yet, Diana adds
 * one afterwards): first name, last name, relationship, phone and email,
 * filled in.
 *
 * Until the parent or carer first signs in, everything can be corrected. A
 * changed email asks first when the family invitation has already gone to
 * the old address (its link stops working); the server supersedes the old
 * nomination, invites the corrected address and tells the old one. After
 * they have signed in, the email is locked with the help line under it.
 *
 * onSaved({ name, emailChanged }) after a save.
 */
export default function HarbourChangeParentModal({ orgId, pupil, parent, onClose, onSaved }) {
  const initial = {
    firstName: parent.firstName || '',
    lastName: parent.lastName || '',
    relationship: parent.relationship || 'parent',
    phone: parent.phone || '',
    email: parent.email || '',
  };
  const [form, setForm] = useState(initial);
  const [step, setStep] = useState('details'); // 'details' | 'confirmEmail'
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState(null);

  const signedIn = parentHasSignedIn(parent);
  const errors = submitted ? parentDetailsErrors(form, { emailLocked: signedIn }) : {};
  const newEmail = form.email.trim();
  const emailChanged = !signedIn && parentEmailChanged(form, parent);
  const currentName = parentFullName(parent) || parent.email || 'the parent or carer';
  const firstName = parent.firstName || 'They';
  const childFirst = (pupil.name || '').split(' ')[0];

  const setField = (field) => (e) => {
    const { value } = e.target;
    setForm((f) => ({ ...f, [field]: value }));
  };

  const send = async () => {
    setBusy(true);
    setFailure(null);
    try {
      const updates = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        relationship: form.relationship,
        phone: form.phone.trim(),
      };
      // The email is only sent when it can change: a signed-in parent's is
      // locked, and the server refuses a change.
      if (!signedIn) updates.email = newEmail;
      const res = await apiUpdateGuardianDetails({
        orgId,
        patientRecordId: pupil.id,
        participantId: parent.participantId,
        updates,
        confirmEmailChange: emailChanged,
      });
      onSaved({
        name: parentFullName(updates) || currentName,
        emailChanged: Boolean(res && res.emailChanged),
      });
    } catch (e) {
      setStep('details');
      setFailure(e?.message || "The parent or carer's details could not be saved. Check your connection and try again.");
    } finally {
      setBusy(false);
    }
  };

  const save = () => {
    setSubmitted(true);
    if (Object.keys(parentDetailsErrors(form, { emailLocked: signedIn })).length) return;
    // Only an address that has had the invitation loses anything: an
    // address never emailed is corrected without the extra step.
    if (emailChanged && parentWasInvited(parent)) {
      setStep('confirmEmail');
      return;
    }
    send();
  };

  if (step === 'confirmEmail') {
    return (
      <HarbourModal
        title={`Change ${currentName}'s email address?`}
        confirmLabel="Change email address"
        busy={busy}
        onConfirm={send}
        onClose={() => setStep('details')}
      >
        <p className="harbour-body-16">
          {/* The old invite is a Firebase email link, which can't be cancelled:
              it still signs the person in, it just no longer reaches the child
              (TDI-803). Say what really happens, not "will stop working". */}
          {`We'll send their invitation to ${newEmail} instead. The link we sent to ${initial.email} will no longer open ${childFirst ? `${childFirst}'s` : "the pupil's"} record.`}
        </p>
      </HarbourModal>
    );
  }

  const eyebrow = [pupil.name, pupil.yearGroup].filter(Boolean).join(' ');
  return (
    <HarbourModal
      title="Change parent or carer"
      eyebrow={eyebrow}
      confirmLabel="Save"
      busy={busy}
      onConfirm={save}
      onClose={onClose}
    >
      <div className="flex flex-col gap-4 text-left">
        <HarbourField
          label="First name"
          value={form.firstName}
          autoComplete="off"
          error={errors.firstName}
          onChange={setField('firstName')}
        />
        <HarbourField
          label="Last name"
          value={form.lastName}
          autoComplete="off"
          error={errors.lastName}
          onChange={setField('lastName')}
        />
        <HarbourField
          as="select"
          label={childFirst ? `Relationship to ${childFirst}` : 'Relationship'}
          value={form.relationship}
          onChange={setField('relationship')}
        >
          {relationshipOptionsFor(initial.relationship).map((r) => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </HarbourField>
        <HarbourField
          label="Phone number"
          hint="Optional"
          type="tel"
          autoComplete="off"
          value={form.phone}
          onChange={setField('phone')}
        />
        {signedIn ? (
          <div className="flex flex-col gap-1">
            <HarbourField
              label="Email address"
              type="email"
              value={form.email}
              readOnly
              aria-readonly="true"
              aria-describedby="harbour-parent-email-locked"
              onChange={() => {}}
            />
            <p id="harbour-parent-email-locked" className="harbour-field__hint">
              {`${firstName} has already signed in, so their email address can't be changed here. If they have a new email address, contact pilot support and we'll help you change it.`}
            </p>
          </div>
        ) : (
          <HarbourField
            label="Email address"
            hint="We send the sign-in email to this address"
            type="email"
            inputMode="email"
            autoCapitalize="none"
            spellCheck={false}
            autoComplete="off"
            value={form.email}
            error={errors.email}
            onChange={setField('email')}
          />
        )}
        {failure && (
          <HarbourNotice kind="error" title="That did not work">{failure}</HarbourNotice>
        )}
      </div>
    </HarbourModal>
  );
}
